import { useNavigate } from 'react-router';
import { Container, Alert } from '@mui/material';
import { useEffect, useState } from 'react';
import { useOidc } from '@nohara-dxdev/react-oidc';

import { ConstructionInfoSection } from '../../components/construction-company/construction-info-section';
import { useConstructionDetailForm } from '../../hooks/construction-company/use-construction-detail-form';
import { companiesService } from '../../services/companies.service';

import DetailForm from './detail-form';

import { useBeforeUnloadWarning } from '@/hooks/use-beforeunload-warning';
import { useSubmitting } from '@/hooks/use-submitting';
import { paths } from '@/config/paths';
import HeaderRegister from '@/features/apps/components/header-register';
import { useSnackbar } from '@/hooks/use-snackbar';
import { MESSAGES } from '@/lib/constants';
import { checkPermission } from '@/utils/helpers';
import { FunctionName, PermissionAction } from '@/lib/enums';

export const ConstructionCompanyDetailPage = () => {
  const navigate = useNavigate();

  // Use the custom hook to handle all form logic
  const {
    // Data
    constructionCompanyId,
    formData,
    originalFormData,
    // State
    isLoading,
    error,

    // Form actions
    updateField,
    addRepresentative,
    removeRepresentative,
    updateRepresentative,
    handleSubmit,

    // Utility functions
    getFieldError,
    isFieldValid,
    getSalesPersonOptions,
    handleDepartmentChange,

    // Computed properties
    canAddRepresentative,
    isSubmitDisabled,
    hasFormChanges,

    // External data
    departments,
    departmentsLoading,
    staffsLoading,
  } = useConstructionDetailForm();
  const { setIsSubmitting } = useSubmitting();

  // Show popup warning when user tries to close/refresh page with unsaved changes
  useBeforeUnloadWarning(hasFormChanges);
  const { setSnackbarMessage } = useSnackbar();

  const handleEdit = async () => {
    const success = await handleSubmit();
    if (success) {
      setIsSubmitting(true);
      navigate(paths.companies.path);
      setSnackbarMessage(MESSAGES.CONSTRUCTION_COMPANY_UPDATE_SUCCESS);
    }
  };

  const handleCancel = () => {
    navigate(paths.companies.path);
  };

  const handleDeleteConstruction = async (): Promise<void> => {
    if (!constructionCompanyId) return;
    await companiesService.deleteConstructionCompany(constructionCompanyId);
    setSnackbarMessage(MESSAGES.DELETE_CONSTRUCTION_COMPANY);
    setIsSubmitting(true);
    navigate(paths.companies.path);
  };

  const { permissions } = useOidc();
  const [canUpdateConstructionCompany, setCanUpdateConstructionCompany] = useState<boolean>(false);
  const [canDeleteConstructionCompany, setCanDeleteConstructionCompany] = useState<boolean>(false);

  // Check permission on mount - redirect to 404 if user doesn't have READ or UPDATE permission
  useEffect(() => {
    const canRead = checkPermission(
      permissions,
      FunctionName.COMPANY_MANAGEMENT,
      PermissionAction.READ
    );
    const canUpdate = checkPermission(
      permissions,
      FunctionName.COMPANY_MANAGEMENT,
      PermissionAction.UPDATE
    );

    if (!canRead && !canUpdate) {
      navigate('/404');
      return;
    }
  }, [permissions, navigate]);
  useEffect(() => {
    setCanUpdateConstructionCompany(formData.canUpdate ?? false);
    setCanDeleteConstructionCompany(formData.canUpdate ?? false);
  }, [formData.canUpdate]);

  return (
    <Container maxWidth='lg' sx={{ py: 4 }}>
      {/* Header */}
      <HeaderRegister title='建設会社詳細' description='建設会社情報の詳細表示・編集' />

      {/* General Error Messages */}
      {error && (
        <Alert severity='error' sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <DetailForm
        canUpdate={canUpdateConstructionCompany}
        canDelete={canDeleteConstructionCompany}
        formData={formData}
        originalFormData={originalFormData}
        getFieldError={getFieldError}
        isFieldValid={isFieldValid}
        addRepresentative={addRepresentative}
        removeRepresentative={removeRepresentative}
        updateRepresentative={updateRepresentative}
        getSalesPersonOptions={getSalesPersonOptions}
        canAddRepresentative={canAddRepresentative}
        departments={departments}
        departmentsLoading={departmentsLoading}
        staffsLoading={staffsLoading}
        handleDepartmentChange={(departmentId) => {
          void handleDepartmentChange(departmentId);
        }}
        isSubmitting={isLoading}
        onDelete={handleDeleteConstruction}
        handleCancel={handleCancel}
        handleEdit={() => {
          void handleEdit();
        }}
        isSubmitDisabled={isSubmitDisabled}
        error={error}
      >
        {/* Construction Company Information Section */}
        <ConstructionInfoSection
          formData={formData}
          originalFormData={originalFormData}
          onUpdateField={updateField}
          getFieldError={getFieldError}
          isFieldValid={isFieldValid}
        />
      </DetailForm>
    </Container>
  );
};
